// THE SHELL'S OVERLAY LAYERS — the windows ModalRoot stack, the DropdownRoot
// layer, the open header search and, in the workspace shell, the assistant's
// MAIN LAYER over a page (read, never written). Returns 'modalRoot' |
// 'dropdown' | 'phoneNav' | 'mobileRail' | 'search' | 'mainThread' | null.
// Read the top layer first. THE ONE reader for every LOWER keyboard layer that must stand down
// while an overlay sits above it: the workspace shortcut guard
// (functions/shortcuts.js shortcutBlockReason), the home customize drawer's
// Escape (pages/main.js) and the marketplace's document keys (marketplace
// functions/index.js registerEscHandler) — a lower layer that spent the
// Escape first would close itself AND leave (with the windows plugin's "a
// modal ignores an Escape a layer above it already spent") or also close
// (without it) the dialog on top.
//
// It lives in the shared kit so EVERY host reaches the same one through its
// registry: the workspace shell and the standalone marketplace
// (marketplace.localhost loads this kit through its sharedLibraries, never the
// workspace functions — with the reader in the workspace only, one Escape
// closed its detail pane under an open install confirm). Moved from workspace
// functions/shortcuts.js (b919b1703).
//
// THE MAIN LAYER (AI_ASSISTANT_SPEC §12.2): with the main placement the
// assistant's thread (or a generated board, a live deck) opens OVER the page
// of any route. While it is the top layer over the page, the page's own
// Escape stands down — ONE Escape closes the thread and the page is back. The
// workspace shell answers it (functions/assistantMainThread.js
// `assistantMainLayerOpen`); a host that registers no such function has no
// such layer. It sits BELOW the layers above, which are read first.
//
// `el.call('shellOverlayOpen')` (this = the element) or with a host / element.
export function shellOverlayOpen(el) {
  const origin = el || this
  const host = (origin && typeof origin.getRoot === 'function' && origin.getRoot()) || origin
  const stack = host && host.ModalRoot && host.ModalRoot.state && host.ModalRoot.state.stack
  if (stack && stack.length > 0) return 'modalRoot'
  if (host && host.DropdownRoot && host.DropdownRoot.state && host.DropdownRoot.state.layer) {
    return 'dropdown'
  }
  // THE HEADER SEARCH (root `_headerSearchOpen`): the search morph sits above
  // the page. Its Escape (HeaderTabs / HeaderSearch) and a page layer's Escape
  // (the home customize drawer) are both document listeners, armed in MOUNT
  // order — when the drawer ran first it closed itself and left the search
  // open (live, dev 3efc1eace, 2026-09-30). A lower layer that asks stands
  // down; the search closes first. Read after the two layers above, which can
  // open over the search.
  const st = (origin && origin.state) || (host && host.state)
  const rootState = (st && st.root) || st
  if (rootState?._isPhoneViewport === true) {
    // The phone navigation dialog sits above the page rail and the header.
    // Desktop ignores old phone flags, including during rotation teardown.
    if (rootState._phoneNavPanel) return 'phoneNav'
    const railOpen = [
      '_calRailOpen',
      '_meetRailOpen',
      '_chatSidebarOpen',
      '_docsRailOpen',
      '_ticketsRailOpen',
      '_filesRailOpen',
      '_mailRailOpen',
      '_moduleRailOpen'
    ].some((key) => !!rootState[key])
    // A direct rail occupies the 1400 band, above header search (1360).
    // A rail inside PageSlot is capped by the pane's 1300 context, so an
    // open header search owns Escape there. The launcher portal is higher
    // than both and already returned 'dropdown' above.
    const paneOpen = rootState._canvasSlot || rootState._canvasSlotSecond
    if (railOpen && !(paneOpen && rootState._headerSearchOpen)) return 'mobileRail'
  }
  if (rootState && rootState._headerSearchOpen) return 'search'
  // THE MAIN LAYER: the lowest of the four — the search (navbar chrome) and
  // the two layers above all sit over it.
  const fns = host && host.context && host.context.functions
  if (
    fns &&
    typeof fns.assistantMainLayerOpen === 'function' &&
    typeof host.call === 'function' &&
    host.call('assistantMainLayerOpen', host)
  ) {
    return 'mainThread'
  }
  return null
}
